import { Box, Button, Breadcrumbs, Checkbox, FormControlLabel, IconButton, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Tooltip, Typography, LinearProgress } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import EditNoteIcon from '@mui/icons-material/EditNote';
import APIInvoke from '../../utils/APIInvoke';
import AddIcon from '@mui/icons-material/Add';
import AppContext from '../../state/AppContext';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDialog from '../../template/ConfirmDialog';
import { toast } from 'react-hot-toast';
import LensIcon from '@mui/icons-material/Lens';
import FilterBuilder from '../../components/FilterBuilder/FilterBuilder';
import TextFilter from '../../components/FilterBuilder/controls/TextFilter';
import { hasText } from '../../utils/StringUtils';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import Header from '../../template/Header';
import SelectFilter from '../../components/FilterBuilder/controls/SelectFilter';
import { fromISOToLocal } from '../../utils/DateUtilsUTC';
import RangeDateFilter from '../../components/FilterBuilder/controls/RangeDateFilter';
import { DateTime } from 'luxon';
import { setLoading } from '../../template/app-actions';
import { useSelector } from 'react-redux';

const filters = [
    {
        id: "timestamp",
        name: "timestamp",
        placeholder: ["From", "To"],
        filter: "timestamp[bw]",
        type: RangeDateFilter,
        defaultValue: "",
        className: "col-span-2",
        value: [DateTime.now().minus({ days: 7 }).startOf("day"), DateTime.now().plus({ day: 1 }).endOf("day")],
        processInput: (value) => {
            let [from, to] = value
            if (to === undefined) {
                to = from
            } else if (from === undefined) {
                from = to
            }
            if (from && to) {
                return [from.toUTC().toFormat("yyyy-MM-dd"), to.plus({ day: 2 }).toUTC().toFormat("yyyy-MM-dd")]
            }
            return ""
        },
        getOptionLabel: (value) => {
            let [from, to] = value
            if (from && to) {
                return `${from.toFormat("yyyy-MM-dd")} - ${to.toFormat("yyyy-MM-dd")}`
            }
            return ""
        }
    }, {
        id: "category",
        name: "category",
        placeholder: "Category",
        filter: "category",
        type: SelectFilter,
        values: [
            {
                key: "USER_MANAGEMENT",
                value: "USER_MANAGEMENT"
            }, {
                key: "COLLECTION_MANAGEMENT",
                value: "COLLECTION_MANAGEMENT"
            },{
                key: "DOCUMENT_MANAGEMENT",
                value: "DOCUMENT_MANAGEMENT"
            },
            {
                key: "PROJECT_MANAGEMENT",
                value: "PROJECT_MANAGEMENT"
            },
            {
                key: "WEBHOOK_MANAGEMENT",
                value: "WEBHOOK_MANAGEMENT"
            }
        ],
        defaultValue: "",
        className: "col-start-1"
    }, {
        id: "type",
        name: "type",
        placeholder: "Type",
        filter: "resource.type",
        type: SelectFilter,
        values: [
            {
                key: "USER",
                value: "USER"
            },
            {
                key: "DOCUMENT",
                value: "DOCUMENT"
            },
            {
                key: "PROJECT",
                value: "PROJECT"
            },{
                key: "WEBHOOK",
                value: "WEBHOOK"
            },
            {
                key: "COLLECTION",
                value: "COLLECTION"
            },
            {
                key: "COLLECTION_RULE",
                value: "COLLECTION_RULE"
            }
        ],
        defaultValue: ""
    }, {
        id: "username",
        name: "username",
        placeholder: "Username",
        filter: "user.username[cn]",
        type: TextFilter,
        defaultValue: ""
    }
]

const columns = [
    {
        field: 'timestamp',
        headerName: 'Timestamp',
        type: 'string',
        align: 'left'
    }, {
        field: 'category',
        headerName: 'Category',
        type: 'string',
        align: 'left'
    }, {
        field: 'type',
        headerName: 'Type',
        type: 'string',
        align: 'left'
    }, {
        field: 'action',
        headerName: 'Action',
        type: 'string',
        align: 'left'
    }, {
        field: 'description',
        headerName: 'Description',
        type: 'string',
        align: 'left'
    }, {
        field: 'user',
        headerName: 'Username',
        type: 'string',
        align: 'left'
    }, {
        field: 'severity',
        headerName: 'Severity',
        type: 'string',
        align: 'left'
    }
]

const initialState = {
    queryBuilder: {
        queryString: `?timestamp[bw]=${DateTime.now().minus({ day: 7 }).startOf("day").toFormat("yyyy-MM-dd")},${DateTime.now().plus({ day: 2 }).endOf("day").toFormat("yyyy-MM-dd")}`
    },
    pageSize: 10,
    totalItems: 0,
    currentPage: 0,
    items: []
}

const Auditlogs = (props) => {

    const navigate = useNavigate();
    const location = useLocation();
    const { appContext } = useContext(AppContext)
    const project = appContext.project
    const [data, setData] = useState(initialState)
    const [showDeleteDialog, setShowDeleteDialog] = useState({ open: false, payload: null })
    const { loading } = useSelector(state => state.pages.app)

    useEffect(() => {
        try {
            setLoading(true)
            init(data)
        } catch (error) {
            console.error("Projects.useEffect => ", error)
        } finally {
            setLoading(false)
        }
    }, [data.currentPage, data.pageSize])

    const getColorSeverity = (severity) => {
        switch (severity) {
            case "LOW": return "text-green-500"
            case "MEDIUM": return "text-yellow-500"
            case "HIGH": return "text-orange-500"
            case "SEVERE": return "text-red-500"
            default: return "text-gray-500"
        }

    }

    const onFilterProcess = async (queryBuilder) => {
        const newState = {
            ...data,
            queryBuilder: queryBuilder,
            totalItems: 0,
            currentPage: 0,
            totalPages: 0,
            pageSize: 10,
        }
        setData(newState)
        init(newState)
    }

    useEffect(() => {
        init(data)
    }, [data.currentPage, data.pageSize])

    const init = async (newState) => {
        try {
            setLoading(true)
            const query = newState.queryBuilder.queryString
            const response = await (await APIInvoke.invokeGET(`/projects/${project}/auditlogs${newState.queryBuilder.queryString}${hasText(query) ? "&" : "?"}page=${newState.currentPage}&projection=!resource.previousState,!resource.newState&size=${newState.pageSize}&sort=timestamp,desc`)).json()
            setData({
                ...newState,
                items: response.body.items,
                totalItems: response.body.totalItems,
                currentPage: response.body.currentPage,
                totalPages: response.body.totalPages,
                pageSize: response.body.pageSize
            })
        } catch (error) {
            console.error("Auditlogs.init => ", error)
        } finally {
            setLoading(false)   

        }

    }

    const onDelete = async () => {
        const response = await (await APIInvoke.invokeDELETE(`/users/${showDeleteDialog.payload.id}`)).json()
        if (response.ok) {
            toast.success(response.message)
            setData({
                ...data,
                items: data.items.filter(x => x.id !== showDeleteDialog.payload.id)
            })
            setShowDeleteDialog({ open: false, payload: null })
        } else {
            toast.error(response.message)
        }
    }

    const onRowClick = (event, row) => {
        navigate(`/projects/${project}/auditlogs/${row.id}`, { state: { backLink: location.pathname } })
    }

    const toggleDeleteDialog = (event, payload) => {
        event.stopPropagation()
        setShowDeleteDialog({
            open: !showDeleteDialog.open,
            payload
        })
    }

    const handleChangePage = (e, newPage) => {
        setData({
            ...data,
            currentPage: newPage
        })
    }

    const onRowsPerPageChange = (event) => {
        setData({
            ...initialState,
            items: data.items,
            pageSize: parseInt(event.target.value, 10)
        })

    }

    return (
        <div className="">
            <ConfirmDialog title="Delete user" message="This action is permanent and there will be no way to recover the user" open={showDeleteDialog.open} onClose={toggleDeleteDialog} onConfirm={onDelete} />
            <Header title="Audit Logs" >
                <Breadcrumbs aria-label="breadcrumb" separator="/" className="!text-gray-600">
                    <Typography ><HomeOutlinedIcon className="text-gray-600" fontSize="small" /></Typography>
                    <Typography ><span className="text-gray-600">Audit Logs</span></Typography>
                </Breadcrumbs>
            </Header>
            <div className="content grid gap-8">
                <div >
                    <Box >
                        <Paper >
                            <div>
                                <FilterBuilder filters={filters} onProcess={onFilterProcess} />
                            </div>
                            {loading && <LinearProgress size="sm" />}
                            <TableContainer>
                                <Table aria-labelledby="tableTitle" size={"small"}  >
                                    <TableHead >
                                        <TableRow >
                                            {columns.map((headCell) => (
                                                <TableCell key={headCell.field} align={headCell.align} padding={'normal'} >
                                                    <TableSortLabel >
                                                        {headCell.headerName}
                                                    </TableSortLabel>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.items.map((row, index) => {
                                            return (
                                                <TableRow
                                                    hover
                                                    onClick={(event) => onRowClick(event, row)}
                                                    tabIndex={-1}
                                                    key={row.id}
                                                    sx={{ cursor: 'pointer' }}
                                                >
                                                    <TableCell align="left">{fromISOToLocal(row.timestamp)}</TableCell>
                                                    <TableCell align="left">{row.category}</TableCell>
                                                    <TableCell align="left">{row.resource.type}</TableCell>
                                                    <TableCell align="left">{row.operation}</TableCell>
                                                    <TableCell align="left">{row.description}</TableCell>
                                                    <TableCell align="left"><rem>{row.user?.username || "anonymous"}</rem></TableCell>
                                                    <TableCell align="left" >
                                                        <p className={getColorSeverity(row.severity)}>{row.severity}</p>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {loading && <LinearProgress size="sm" />}
                            <TablePagination
                                rowsPerPageOptions={[10, 20, 50, 100]}
                                component="div"
                                rowsPerPage={data.pageSize}
                                count={data.totalItems}
                                page={data.currentPage}
                                disabled={loading}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={onRowsPerPageChange}
                            />
                        </Paper>
                    </Box>
                </div>
            </div>
        </div>
    )
}

export default Auditlogs 